// HTTP Client with Auth Interceptors

import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosError, type AxiosResponse } from 'axios';
import { getConfig, isConfigLoaded } from '@/shared/config/runtime';

interface IRefreshTokenResponse {
  readonly accessToken: string;
  /**
   * Продление ротирует обновление-токен: сервер возвращает новый, прежний
   * становится недействительным. Клиент обязан сохранить полученный —
   * иначе следующее продление получит отказ «неверный токен».
   */
  readonly refreshToken: string;
}

const DEFAULT_API_BASE_URL = '/api/v1';

/**
 * Получает базовый URL для API
 * Приоритет: runtime config > env variable > default
 */
function getApiBaseUrl(): string {
  // Если runtime конфиг загружен, используем его
  if (isConfigLoaded()) {
    const runtimeConfig = getConfig();
    if (runtimeConfig.apiBaseUrl !== undefined && runtimeConfig.apiBaseUrl.length > 0) {
      return runtimeConfig.apiBaseUrl;
    }
  }

  // Fallback на env variable
  const apiUrl: unknown = import.meta.env['VITE_API_URL'];
  if (typeof apiUrl === 'string' && apiUrl.trim().length > 0) {
    return apiUrl;
  }

  // Fallback на default
  return DEFAULT_API_BASE_URL;
}

// Create axios instance
// Инициализируем с дефолтным URL, будет обновлен после загрузки конфига
export const httpClient: AxiosInstance = axios.create({
  baseURL: DEFAULT_API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Обновляет базовый URL после загрузки runtime конфигурации
 * Вызывается автоматически из main.ts после loadConfig()
 */
export function updateApiBaseUrl(): void {
  const baseURL = getApiBaseUrl();
  httpClient.defaults.baseURL = baseURL;
  if (import.meta.env.DEV) {
    console.info(`API base URL set to: ${baseURL}`);
  }
}

/**
 * Возвращает Authorization header из localStorage (если токен есть).
 * Используется как Axios interceptor-ом, так и fetch-based клиентами.
 */
export function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  if (token !== null && token.length > 0) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

// ============================================================================
// Action Group — groups multiple mutations into a single undo operation
// ============================================================================

let currentActionGroupId: string | null = null;

/**
 * Set action group ID for the next batch of mutations.
 * All HTTP requests while this is set will include X-Action-Group-Id header,
 * grouping them into a single undo operation.
 */
export function setActionGroupId(id: string): void {
  currentActionGroupId = id;
}

/** Clear the current action group ID. */
export function clearActionGroupId(): void {
  currentActionGroupId = null;
}

// Request interceptor: Add Authorization header + X-Edit-Session header + X-Action-Group-Id
httpClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const headers = getAuthHeaders();
    if (headers['Authorization'] !== undefined) {
      config.headers.Authorization = headers['Authorization'];
    }
    // Add edit session header if available
    const editSessionId = sessionStorage.getItem('editSessionClientId');
    if (editSessionId !== null && editSessionId.length > 0) {
      config.headers['X-Edit-Session'] = editSessionId;
    }
    // Add action group header if set
    if (currentActionGroupId !== null) {
      config.headers['X-Action-Group-Id'] = currentActionGroupId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: Auto-refresh token on 401
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeTokenRefresh(callback: (token: string) => void): void {
  refreshSubscribers.push(callback);
}

function onTokenRefreshed(token: string): void {
  refreshSubscribers.forEach((callback) => {
    callback(token);
  });
  refreshSubscribers = [];
}

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    // If 401 and not a retry
    if (
      error.response?.status === 401 &&
      originalRequest !== undefined &&
      originalRequest._retry !== true
    ) {
      // Don't try to refresh token for public endpoints (login, refresh)
      const url = originalRequest.url ?? '';
      const isPublicEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh');
      
      if (isPublicEndpoint) {
        // Just reject for public endpoints - this is expected for invalid credentials
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(httpClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken === null || refreshToken.length === 0) {
          throw new Error('No refresh token');
        }

        // Call refresh endpoint (without interceptor to avoid infinite loop)
        const { data } = await axios.post<IRefreshTokenResponse>(
          `${getApiBaseUrl()}/auth/refresh`,
          { refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );

        const newAccessToken = data.accessToken;
        localStorage.setItem('accessToken', newAccessToken);
        // Обновление-токен ротирован: сохраняем новый, прежний больше не примут
        if (typeof data.refreshToken === 'string' && data.refreshToken.length > 0) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }

        // Notify all queued requests
        onTokenRefreshed(newAccessToken);

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return httpClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - logout user
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        // Используем относительный путь для поддержки subpath deployment
        const config = getConfig();
        const basePath = config.basePath ?? '/';
        window.location.href = `${basePath}${basePath.endsWith('/') ? '' : '/'}login`;
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// Mutation callback — allows features to react to successful mutating API calls
// ============================================================================

/** Path segments belonging to changelog endpoints — must NOT trigger mutation callback */
const CHANGELOG_PATH_SEGMENTS = new Set(['edit-sessions', 'undo', 'redo']);

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

type TMutationCallback = () => void;
let mutationCallback: TMutationCallback | null = null;

/**
 * Register a callback invoked after every successful mutating API call
 * (POST/PUT/PATCH/DELETE with X-Edit-Session, excluding changelog endpoints).
 */
export function setMutationCallback(cb: TMutationCallback): void {
  mutationCallback = cb;
}

/** Unregister the mutation callback. */
export function clearMutationCallback(): void {
  mutationCallback = null;
}

function isChangelogUrl(url: string): boolean {
  return url.split('/').some((segment) => CHANGELOG_PATH_SEGMENTS.has(segment));
}

// Response interceptor: notify about successful mutations
httpClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const method = (response.config.method ?? '').toLowerCase();
    const url = response.config.url ?? '';
    const hasEditSession = response.config.headers['X-Edit-Session'] !== undefined;

    if (
      mutationCallback !== null &&
      hasEditSession &&
      MUTATING_METHODS.has(method) &&
      !isChangelogUrl(url)
    ) {
      mutationCallback();
    }

    return response;
  },
);
